import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { makeStyles } from "@material-ui/core";
import {
  usePlByProgramStyles,
  PlPageHeader,
  PlChartPanel,
  PlMultiSelect,
} from "./plByProgramTheme";
import { useReportFonts } from "../../reportFonts";
import {
  getForecastVersionsComparisonAPI,
  getVersionsRawAPI,
} from "../../../../api/reports-api";
import {
  getApiFilterKey,
  getGlobalPeriodLabels,
  formatActualsThroughDate,
  formatActualsThroughFromLabel,
  headerActualsDate,
} from "./plTimeFilterUtils";
import Loading from "../../../../common-components/Loading";
import { toReportApiFilters } from "../filterRelationshipUtils";
import {
  AD_NAVY,
  AD_SKY,
  AD_MIDNIGHT,
  AD_SLATE,
  AD_MIST,
  CHART_DONUT_COLORS,
} from "../../reportColorPalette";
import { VERSION_OPTIONS_FALLBACK } from "../../versionOrder";

const NAV = AD_MIDNIGHT;
const FONT = '"Roboto", "Helvetica", "Arial", sans-serif';
const TOGGLE_ON = AD_SKY;
const TOGGLE_ON_BORDER = AD_NAVY;
const MUTED = AD_SLATE;
const MUTED_LT = AD_MIST;

const GROUPED_BAR_COLORS = CHART_DONUT_COLORS;

/* All 11 metrics — shown in the dropdown regardless of what the API returns */
const ROW_DEFS_ALL = [
  { key: "contractRevenue", label: "Revenue", isPct: false },
  { key: "directLabor", label: "Direct Labor", isPct: false },
  { key: "subcontractor", label: "Subcontractor", isPct: false },
  { key: "odcs", label: "ODCs", isPct: false },
  { key: "totalDirectCost", label: "Total Direct Cost", isPct: false },
  { key: "grossMargin", label: "Gross Margin", isPct: false },
  { key: "grossMarginPct", label: "Gross Margin %", isPct: true },
  { key: "indirectCost", label: "Indirect Cost", isPct: false },
  { key: "totalCost", label: "Total Cost", isPct: false },
  { key: "netProfit", label: "Net Profit / Fee", isPct: false },
  { key: "netMarginPct", label: "Net Margin %", isPct: true },
];
const MONTH_OPTS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const QUARTER_OPTS = ["Q1", "Q2", "Q3", "Q4"];

/** Latest month present in actualColumns (ignores YTD/Total/quarter entries). */
function getLastActualMonth(actualColumns) {
  if (!actualColumns?.length) return null;
  const monthKeysLc = MONTH_OPTS.map((m) => m.toLowerCase());
  const set = new Set(actualColumns.map((c) => String(c).toLowerCase()));
  const found = [...monthKeysLc].reverse().find((m) => set.has(m));
  return found ? found[0].toUpperCase() + found.slice(1) : null;
}

/* Fallback versions shown while API loads — canonical order */
const VERSION_FALLBACK = VERSION_OPTIONS_FALLBACK;
const MAX_COMPARE_SELECTIONS = 3;
const MAX_VERSION_SELECTIONS = 5;

/* Default version comparison: the first 3 versions /reports/versions returns,
   in whatever order the API provides them (e.g. Live EAC, Budget, latest
   quarter) — never a fixed key list. This empty base is just the initial
   pre-fetch placeholder, replaced once the versions-fetch effect below
   resolves (see DEFAULT_VERSION_COUNT). */
const DEFAULT_VERSION_KEYS = [];
const DEFAULT_VERSION_COUNT = 3;
const TAB_ACTIVE = "#4338CA";

function versionKeysEqual(a = [], b = []) {
  const norm = (keys) => [...keys].map(String).sort();
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
}

function buildApiFilters(filters) {
  return toReportApiFilters(filters, [
    "year", "contractId", "contractName", "customerName", "projectId", "orgId",
    "bu", "contractType", "clinLvl2", "businessType", "awardType", "competitionType",
    "popEndDate",
  ]);
}

/** Load chart data for one or more selected versions (reuses pairwise API). */
async function fetchVersionsComparisonData(filters, versionKeys) {
  if (!versionKeys?.length) return null;
  const apiFilters = buildApiFilters(filters);

  if (versionKeys.length === 1) {
    const key = versionKeys[0];
    const res = await getForecastVersionsComparisonAPI(apiFilters, key, key);
    if (res?.status !== 200 || !res?.data) return null;
    const d = res.data;
    const metrics = d.left ?? d.right;
    if (!metrics) return null;
    return {
      rows: d.rows,
      actualColumns: d.actualColumns ?? [],
      actualThroughMonth: d.actualThroughMonth ?? null,
      versions: [
        {
          key,
          name: d.leftVersion?.name ?? d.rightVersion?.name ?? key,
          metrics,
          actualColumns: d.actualColumns ?? [],
        },
      ],
    };
  }

  const anchor = versionKeys[0];
  const others = versionKeys.slice(1);
  const results = await Promise.all(
    others.map((v) => getForecastVersionsComparisonAPI(apiFilters, anchor, v)),
  );

  const first = results[0];
  if (first?.status !== 200 || !first?.data?.left) return null;

  const versions = [
    {
      key: anchor,
      name: first.data.leftVersion?.name ?? anchor,
      metrics: first.data.left,
      actualColumns: first.data.actualColumns ?? [],
    },
  ];

  others.forEach((v, i) => {
    const res = results[i];
    if (res?.status === 200 && res?.data?.right) {
      versions.push({
        key: v,
        name: res.data.rightVersion?.name ?? v,
        metrics: res.data.right,
        actualColumns:
          res.data.rightActualColumns ?? res.data.actualColumns ?? [],
      });
    }
  });

  return {
    rows: first.data.rows,
    actualColumns: first.data.actualColumns ?? [],
    actualThroughMonth: first.data.actualThroughMonth ?? null,
    versions,
  };
}

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                         */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  toggleWrap: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  toggleLabel: {
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: "nowrap",
    transition: "color 0.2s",
  },
  toggleTrack: {
    position: "relative",
    width: 44,
    height: 22,
    borderRadius: 11,
    background: MUTED_LT,
    cursor: "pointer",
    flexShrink: 0,
    border: `1px solid ${MUTED}`,
    transition: "background 0.2s, border-color 0.2s",
  },
  toggleThumb: {
    position: "absolute",
    top: 2,
    width: 16,
    height: 16,
    borderRadius: "50%",
    background: NAV,
    boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
    transition: "left 0.2s, background 0.2s",
  },
  versionApplyRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  versionApplyBtn: {
    height: 36,
    padding: "0 16px",
    borderRadius: 28,
    border: "none",
    background: TAB_ACTIVE,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: FONT,
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { background: "#4338CA" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
    "&:disabled:hover": { background: TAB_ACTIVE },
  },
}));

/* ══════════════════════════════════════════════════════════════ */
/*  ViewToggle — Month / Quarter                                   */
/* ══════════════════════════════════════════════════════════════ */
function ViewToggle({ viewMode, onChange, classes }) {
  const isQuarter = viewMode === "Quarter";
  return (
    <div className={classes.toggleWrap}>
      <span
        className={classes.toggleLabel}
        style={{ color: !isQuarter ? NAV : MUTED }}
      >
        Month
      </span>
      <div
        className={classes.toggleTrack}
        onClick={() => onChange(isQuarter ? "Month" : "Quarter")}
        title="Toggle Month / Quarter"
      >
        <div
          className={classes.toggleThumb}
          style={{ left: isQuarter ? 24 : 2 }}
        />
      </div>
      <span
        className={classes.toggleLabel}
        style={{ color: isQuarter ? NAV : MUTED }}
      >
        Quarter
      </span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  ShowTotalToggle — optional Total column on chart               */
/* ══════════════════════════════════════════════════════════════ */
function ShowTotalToggle({ enabled, onChange, classes }) {
  return (
    <div className={classes.toggleWrap}>
      <span
        className={classes.toggleLabel}
        style={{ color: !enabled ? NAV : MUTED }}
      >
        Show Total
      </span>
      <div
        className={classes.toggleTrack}
        style={{
          background: enabled ? TOGGLE_ON : MUTED_LT,
          borderColor: enabled ? TOGGLE_ON_BORDER : MUTED,
        }}
        onClick={() => onChange(!enabled)}
        title="Show Total Bar"
        role="switch"
        aria-checked={enabled}
      >
        <div
          className={classes.toggleThumb}
          style={{
            left: enabled ? 24 : 2,
            background: enabled ? "#FFFFFF" : NAV,
          }}
        />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Number formatters                                              */
/* ══════════════════════════════════════════════════════════════ */
function fmtMoney(v, decimals = 2) {
  if (v === null || v === undefined) return "—";
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000)
    return `${sign}$${(abs / 1_000_000_000).toFixed(decimals)}B`;
  if (abs >= 1_000_000)
    return `${sign}$${(abs / 1_000_000).toFixed(decimals)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

function fmtMoneyCompact(v) {
  if (v === null || v === undefined) return "—";
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000)
    return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(0)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

function fmtChartBarLabel(v, isPct = false) {
  if (v === null || v === undefined) return "";
  if (isPct) return `${Number(v).toFixed(1)}%`;
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000)
    return `${sign}$${(abs / 1_000_000_000).toFixed(2)} B`;
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)} M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(2)} K`;
  return `${sign}$${abs.toFixed(0)}`;
}

/** Grouped bar label — vertical text directly above each bar (reference layout) */
function groupedBarDataPoint(value, barColor, fmtVal, labelOpts = {}) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) {
    return { value: n, label: { show: false } };
  }

  return {
    value: n,
    label: {
      show: true,
      position: "top",
      rotate: 90,
      align: "left",
      verticalAlign: "middle",
      distance: labelOpts.distance ?? 2,
      fontSize: labelOpts.fontSize ?? 8,
      fontWeight: 600,
      color: barColor,
      fontFamily: FONT,
      formatter: () => fmtVal(n),
    },
  };
}

function fmtMoneyAxis(v) {
  if (v === null || v === undefined) return "";
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000)
    return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

/* ══════════════════════════════════════════════════════════════ */
/*  Chart option builder (uses API data)                           */
/* ══════════════════════════════════════════════════════════════ */
function buildChartOption({
  versionData,
  effectiveKeys,
  rowDefs,
  selectedPeriods,
}) {
  const versions = versionData?.versions ?? [];
  if (!versions.length || !effectiveKeys?.length) return {};

  const seriesCount = versions.length * effectiveKeys.length;
  const density = seriesCount;
  const labelFontSize =
    density <= 4 ? 9 : density <= 8 ? 8 : density <= 14 ? 7 : 6;
  const barMaxWidth = Math.max(
    8,
    Math.min(
      24,
      Math.floor(480 / (selectedPeriods.length * Math.max(seriesCount, 1) * 1.15)),
    ),
  );

  const fallbackActualKeys = (() => {
    const atm = Number(versionData?.actualThroughMonth) || 0;
    if (atm < 1) return [];
    return MONTH_OPTS.slice(0, Math.min(atm, 12)).map((m) => m.toLowerCase());
  })();

  let dataMax = 0;
  versions.forEach((ver) => {
    effectiveKeys.forEach((key) => {
      const def = rowDefs.find((r) => r.key === key) ?? {};
      const isPct = def.isPct ?? false;
      selectedPeriods.forEach((period) => {
        const v = (ver.metrics?.[key] ?? {})[period.toLowerCase()] ?? 0;
        const val = isPct ? Math.abs(v * 100) : Math.abs(v);
        if (val > dataMax) dataMax = val;
      });
    });
  });
  const yMax = dataMax > 0 ? dataMax * 1.12 : undefined;

  const seriesDefs = [];
  versions.forEach((ver, vIdx) => {
    const cols = ver.actualColumns?.length
      ? ver.actualColumns
      : fallbackActualKeys;
    const actualSet = new Set(cols.map((c) => String(c).toLowerCase()));

    effectiveKeys.forEach((key, idx) => {
      const def = rowDefs.find((r) => r.key === key) ?? {};
      const isPct = def.isPct ?? false;
      const seriesIndex = vIdx * effectiveKeys.length + idx;
      const color =
        GROUPED_BAR_COLORS[seriesIndex % GROUPED_BAR_COLORS.length];
      const label = def.label ?? key;
      const seriesName =
        versions.length > 1 && effectiveKeys.length > 1
          ? `${ver.name} ${label}`
          : versions.length > 1
            ? ver.name
            : label;

      const getVal = (periodLabel) => {
        const v = (ver.metrics?.[key] ?? {})[periodLabel.toLowerCase()] ?? 0;
        return isPct ? +(v * 100).toFixed(1) : v;
      };

      seriesDefs.push({
        seriesName,
        color,
        isPct,
        actualSet,
        metricKey: key,
        metricLabel: label,
        versionName: ver.name,
        values: selectedPeriods.map((period) => getVal(period)),
      });
    });
  });

  const seriesArr = [];
  const seriesMeta = [];
  const legendData = [];

  seriesDefs.forEach((def, si) => {
    const fmtVal = (v) => fmtChartBarLabel(v, def.isPct);

    seriesMeta.push({
      versionName: def.versionName,
      isPct: def.isPct,
      actualSet: def.actualSet,
      metricKey: def.metricKey,
      metricLabel: def.metricLabel,
    });
    seriesArr.push({
      name: def.seriesName,
      type: "bar",
      barMaxWidth,
      barGap: "8%",
      clip: false,
      itemStyle: { color: def.color },
      data: def.values.map((val) =>
        groupedBarDataPoint(val, def.color, fmtVal, {
          fontSize: labelFontSize,
        }),
      ),
    });
    legendData.push({ name: def.seriesName, itemStyle: { color: def.color } });
  });

  return {
    grid: { top: 88, right: 20, bottom: 36, left: 20, containLabel: true },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      backgroundColor: "#fff",
      borderColor: "#E2E8F0",
      borderWidth: 1,
      textStyle: { fontSize: 11, color: NAV, fontFamily: FONT },
      formatter: (params) => {
        if (!params?.length) return "";
        const period = params[0].axisValue;
        const periodKey = period.toLowerCase();
        const hdr = `<b style="font-size:11px">${period}</b><br/>`;
        const byMetricKey = {};
        params.forEach((p) => {
          const meta = seriesMeta[p.seriesIndex] ?? {};
          const mk = meta.metricKey || p.seriesName;
          if (!byMetricKey[mk]) byMetricKey[mk] = [];
          byMetricKey[mk].push(p);
        });
        const lines = effectiveKeys.map((key) => {
          const metricDef = rowDefs.find((r) => r.key === key) ?? {};
          const label = metricDef.label ?? key;
          const pts = byMetricKey[key] ?? [];
          if (!pts.length) return "";
          const isPct = metricDef.isPct ?? false;
          const fmt = (v) =>
            v != null
              ? isPct
                ? `${Number(v).toFixed(1)}%`
                : fmtMoney(v)
              : "—";
          const meta0 = seriesMeta[pts[0].seriesIndex] ?? {};
          const isAct = meta0.actualSet?.has?.(periodKey);
          const type = isAct ? "Actual" : "Forecast";
          const versionLines = pts
            .map((p) => {
              const meta = seriesMeta[p.seriesIndex] ?? {};
              return `${p.marker} ${meta.versionName}: <b>${fmt(p.value)}</b>`;
            })
            .join("<br/>");
          return `<b>${label}</b> <span style="color:${MUTED};font-size:10px">(${type})</span><br/>${versionLines}`;
        });
        return hdr + lines.filter(Boolean).join("<br/>");
      },
    },
    legend: {
      top: 8,
      type: "scroll",
      itemWidth: 8,
      itemHeight: 8,
      icon: "circle",
      itemGap: 10,
      textStyle: { fontSize: 10, color: MUTED, fontFamily: FONT },
      data: legendData,
    },
    xAxis: {
      type: "category",
      data: selectedPeriods,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        fontSize: 10,
        color: MUTED,
        fontWeight: 500,
        fontFamily: FONT,
      },
    },
    yAxis: {
      type: "value",
      max: yMax,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: "#F1F5F9", type: "dashed" } },
      axisLabel: {
        fontSize: 9,
        color: MUTED,
        fontFamily: FONT,
        formatter: fmtMoneyAxis,
      },
    },
    series: seriesArr,
  };
}

/* ══════════════════════════════════════════════════════════════ */
/*  Main component                                                 */
/* ══════════════════════════════════════════════════════════════ */
export default function ForecastVersionsComparison({
  filters,
  actualsThroughDate,
}) {
  const plClasses = usePlByProgramStyles();
  const viewClasses = useStyles();
  useReportFonts();
  const classes = plClasses;

  /* ── API / version state ── */
  const [versionData, setVersionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftVersionKeys, setDraftVersionKeys] = useState(DEFAULT_VERSION_KEYS);
  const [appliedVersionKeys, setAppliedVersionKeys] = useState(DEFAULT_VERSION_KEYS);
  const hasAppliedDefaultVersions = useRef(false);
  /* Gates the chart-data fetch until the default-version reconciliation below has
     resolved, so there isn't a wasted fetch against defaults that turn out unavailable. */
  const [versionsReady, setVersionsReady] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(["contractRevenue"]);
  const [versionOptions, setVersionOptions] = useState(VERSION_FALLBACK);

  /* ── UI state (no refetch) ── */
  const [viewMode, setViewMode] = useState("Month");
  const [showTotalBar, setShowTotalBar] = useState(false);

  const selectedPeriods = useMemo(() => {
    const fromGlobal = getGlobalPeriodLabels(filters, viewMode);
    if (fromGlobal?.length) return fromGlobal;
    return viewMode === "Month" ? MONTH_OPTS : QUARTER_OPTS;
  }, [filters?.monthName, filters?.quarter, viewMode]);

  const chartPeriods = useMemo(
    () => (showTotalBar ? [...selectedPeriods, "Total"] : selectedPeriods),
    [selectedPeriods, showTotalBar],
  );

  const year =
    filters?.year && filters.year !== "All"
      ? filters.year
      : String(new Date().getFullYear());
  const apiFilterKey = useMemo(
    () => getApiFilterKey(filters, buildApiFilters),
    [filters],
  );

  /* Fetch available versions when year changes; default the version selection
     to the first DEFAULT_VERSION_COUNT entries the API returns for that year,
     without overriding a user's own selection. */
  useEffect(() => {
    getVersionsRawAPI(year)
      .then((res) => {
        if (res?.data?.length) {
          const rawData = res.data;
          /* Backend's own response order, both for the dropdown display and
             the default pick below — not the canonical sort, which would show
             q1 before q2 regardless of which one the API actually leads with. */
          const opts = rawData.map((v) => ({ value: v.key, label: v.name }));
          setVersionOptions(opts);
          if (!hasAppliedDefaultVersions.current) {
            const available = rawData
              .slice(0, DEFAULT_VERSION_COUNT)
              .map((v) => v.key);
            if (available.length) {
              hasAppliedDefaultVersions.current = true;
              const reconcileDefaults = (prev) =>
                versionKeysEqual(prev, DEFAULT_VERSION_KEYS) ? available : prev;
              setDraftVersionKeys(reconcileDefaults);
              setAppliedVersionKeys(reconcileDefaults);
            }
          }
        }
      })
      .catch(() => {})
      .finally(() => setVersionsReady(true));
  }, [year]);

  const effectiveVersionKeys = useMemo(
    () => appliedVersionKeys.slice(0, MAX_VERSION_SELECTIONS),
    [appliedVersionKeys],
  );

  /* Fetch chart data when global filters or applied versions change */
  useEffect(() => {
    if (!versionsReady || !effectiveVersionKeys.length) return;
    let cancelled = false;
    setLoading(true);
    fetchVersionsComparisonData(filters, effectiveVersionKeys)
      .then((data) => {
        if (!cancelled) setVersionData(data);
      })
      .catch(() => {
        if (!cancelled) setVersionData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiFilterKey, effectiveVersionKeys.join("|"), versionsReady]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Derived values ── */
  const apiRowDefs = versionData?.rows ?? [];
  const rowDefs = (apiRowDefs.length > 0 ? apiRowDefs : ROW_DEFS_ALL).map(
    (r) => ({ ...(ROW_DEFS_ALL.find((d) => d.key === r.key) ?? r), ...r }),
  );

  const versionLabelOptions = versionOptions.map((v) => v.label);
  const draftVersionLabels = draftVersionKeys
    .map((k) => versionOptions.find((v) => v.value === k)?.label)
    .filter(Boolean);
  const handleVersionChange = useCallback(
    (labels) => {
      const keys = labels
        .map((l) => versionOptions.find((v) => v.label === l)?.value)
        .filter(Boolean)
        .slice(0, MAX_VERSION_SELECTIONS);
      setDraftVersionKeys(keys);
    },
    [versionOptions],
  );
  const handleVersionApply = useCallback(() => {
    setAppliedVersionKeys(draftVersionKeys.slice(0, MAX_VERSION_SELECTIONS));
  }, [draftVersionKeys]);
  const versionApplyDisabled = versionKeysEqual(
    draftVersionKeys.slice(0, MAX_VERSION_SELECTIONS),
    appliedVersionKeys.slice(0, MAX_VERSION_SELECTIONS),
  );

  const metricLabelOptions = rowDefs.map((r) => r.label);
  const selectedLabels = selectedKeys
    .map((k) => rowDefs.find((r) => r.key === k)?.label)
    .filter(Boolean);
  const handleMetricChange = (labels) => {
    const keys = labels
      .map((l) => rowDefs.find((r) => r.label === l)?.key)
      .filter(Boolean)
      .slice(0, MAX_COMPARE_SELECTIONS);
    setSelectedKeys(keys);
  };
  const effectiveKeys = selectedKeys.slice(0, MAX_COMPARE_SELECTIONS);

  const versionNames = (versionData?.versions ?? []).map((v) => v.name);
  const chartTitle = versionNames.length
    ? `${versionNames.join(" · ")} (${viewMode === "Month" ? "Monthly" : "Quarterly"} View)`
    : `Forecast Versions (${viewMode === "Month" ? "Monthly" : "Quarterly"} View)`;
  const chartOption = buildChartOption({
    versionData,
    effectiveKeys,
    rowDefs,
    selectedPeriods: chartPeriods,
  });

  const lastActualLabel = getLastActualMonth(versionData?.actualColumns);
  const headerDate = headerActualsDate(
    filters?.year,
    loading,
    formatActualsThroughDate(versionData?.actualThroughMonth),
    formatActualsThroughFromLabel(lastActualLabel),
  );

  if (loading) return <Loading />;

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        {/* ── Page header ── */}
        <PlPageHeader
          classes={classes}
          title="Forecast Versions - Comparison"
          accent="Version Analysis"
          meta="Compare Forecast Scenarios"
          metaTop={`FY-${year}`}
          date={headerDate}
        />

        <div className={classes.sectionCard}>
          {/* ── Filter bar ── */}
          <div className={classes.filterBar}>
            <ViewToggle
              viewMode={viewMode}
              onChange={setViewMode}
              classes={viewClasses}
            />

            {/* Metric selector */}
            <PlMultiSelect
              label="Metric"
              options={metricLabelOptions}
              value={selectedLabels}
              onChange={handleMetricChange}
              classes={classes}
              maxSelected={MAX_COMPARE_SELECTIONS}
            />

            <div className={viewClasses.versionApplyRow}>
              <PlMultiSelect
                label="Version"
                options={versionLabelOptions}
                value={draftVersionLabels}
                onChange={handleVersionChange}
                classes={classes}
                maxSelected={MAX_VERSION_SELECTIONS}
              />
              <button
                type="button"
                className={viewClasses.versionApplyBtn}
                onClick={handleVersionApply}
                disabled={versionApplyDisabled}
              >
                Apply
              </button>
            </div>

            <div style={{ flex: 1 }} />

            <ShowTotalToggle
              enabled={showTotalBar}
              onChange={setShowTotalBar}
              classes={viewClasses}
            />
          </div>

          {/* ── Chart ── */}
          <div className={classes.chartPanelStack}>
            <PlChartPanel
              title={chartTitle}
              option={chartOption}
              classes={classes}
              panelClassName={classes.chartPanelLg}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
